import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Master Data Bulk Insert Step
 *
 * Inserts parsed rows into a typed master data table (e.g. rexel_invoice_master,
 * rexel_item_master) using bulkCreate with upsert (updateOnDuplicate).
 *
 * For large files (600K+ rows), configure batchSize and maxRows to control
 * throughput and DB pressure.
 *
 * Example workflow:
 * ```
 * Schedule Trigger → Blob Storage Fetch → Parse File → Master Data Bulk Insert
 * ```
 */
export const masterDataBulkInsertStep = defineStepConfig({
  type: "master_data_bulk_insert",
  name: "Master Data Bulk Insert",
  description:
    "Insert parsed rows into a typed master data table with upsert support",
  category: "Data Access",

  inputSchema: z.object({
    rowsBlobKey: z
      .string()
      .describe("Scratch blob key containing parsed rows NDJSON"),
    containerName: z
      .string()
      .optional()
      .describe("Storage container (defaults to system default)"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile to use (propagated from upstream)"),
    fileName: z
      .string()
      .optional()
      .describe(
        "Original file name (used with fileNameFieldExtractor to inject fields into each row)",
      ),
    fileBlobKey: z
      .string()
      .optional()
      .describe(
        "Source blob key (e.g. RDtogains/RDvendmast.txt). Preferred over " +
          "fileName for fileNameFieldExtractor because the blob key is " +
          "per-fanout-correct while fileName can be inherited from a single " +
          "scalar emitted upstream.",
      ),
    lastModified: z
      .string()
      .optional()
      .describe(
        "Source blob lastModified ISO timestamp (used with dateFromLastModified to inject a date column on every row)",
      ),
  }),

  outputSchema: z.object({
    insertedCount: z.number().describe("Number of rows inserted (new records)"),
    updatedCount: z
      .number()
      .describe(
        "Number of rows updated (existing records matched by unique key)",
      ),
    errorCount: z.number().describe("Number of rows that failed to insert"),
    nullKeySkipCount: z
      .number()
      .describe(
        "Number of rows skipped because a required conflict key was null or undefined (expected for sparse source data)",
      ),
    skippedCount: z
      .number()
      .describe("Number of rows skipped (exceeded maxRows limit)"),
    totalRows: z.number().describe("Total rows read from the blob"),
    startedAt: z
      .string()
      .describe(
        "ISO timestamp captured before the first insert — use to scope downstream queries to only rows touched by this run",
      ),
    company: z
      .string()
      .optional()
      .describe(
        "The single company code shared by every row this run wrote. Undefined " +
          "when the target model has no company column or the rows disagreed. " +
          "Wire alongside startedAt to scope downstream steps to this run's slice.",
      ),
    division: z
      .string()
      .optional()
      .describe(
        "The single division code shared by every row this run wrote. Undefined " +
          "when the target model has no division column or the rows disagreed. " +
          "Wire alongside startedAt to scope downstream steps to this run's slice.",
      ),
  }),

  configSchema: z.object({
    targetModel: z
      .string()
      .min(1)
      .describe(
        "Target database table name (must match a registered model in the handler, e.g. rexel_invoice_master)",
      ),
    uniqueKeyFields: z
      .array(z.string())
      .min(1, { message: "At least one unique key field is required" })
      .describe(
        "Column names (snake_case) that form the unique constraint for upsert",
      ),
    duplicateStrategy: z
      .enum(["update", "skip"])
      .prefault("update")
      .describe(
        "What to do when a row matches an existing unique key. " +
          '"update" overwrites the existing row with new data. ' +
          '"skip" leaves the existing row untouched.',
      ),
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe(
        "Number of rows per database INSERT. " +
          "Higher = fewer round-trips but larger queries. Max 5000.",
      ),
    concurrentBatches: z
      .number()
      .min(1)
      .max(10)
      .prefault(1)
      .describe(
        "Number of batches to process concurrently within each buffer flush. " +
          "1 = sequential (default). " +
          "Higher values reduce wall-clock time by overlapping DB round-trips. Max 10.",
      ),
    maxRows: z
      .number()
      .min(0)
      .prefault(0)
      .describe(
        "Maximum number of rows to process. 0 = unlimited. " +
          "Use for testing or throttling large files.",
      ),
    fileNameFieldExtractor: z
      .object({
        pattern: z
          .string()
          .refine(
            (p) => {
              try {
                new RegExp(p);
                return true;
              } catch {
                return false;
              }
            },
            { message: "Invalid regex pattern" },
          )
          .describe(
            "Regex pattern with capture groups to extract fields from the file name. " +
              "E.g. 'E844P_(\\\\w)_(\\\\w)_' extracts company and division.",
          ),
        fields: z
          .array(z.string())
          .describe(
            "Field names (snake_case) to assign to each capture group, in order. " +
              "E.g. ['company', 'division']",
          ),
      })
      .optional()
      .describe(
        "Extract fields from the input fileName and inject them into every row before insert. " +
          "Useful when the file data doesn't contain company/division but the filename does.",
      ),
    dateFromLastModified: z
      .object({
        field: z
          .string()
          .describe(
            "Snake-case column name to write the truncated date to (e.g. 'snapshot_date').",
          ),
        timezone: z
          .string()
          .prefault("UTC")
          .describe(
            "IANA timezone for date truncation (e.g. 'America/New_York'). " +
              "The blob lastModified timestamp is converted to a calendar date in this timezone.",
          ),
      })
      .optional()
      .describe(
        "Inject a DATE column on every row, derived from the source blob's lastModified timestamp. " +
          "Useful for snapshot files (e.g. ONHNUNSH) where the file represents a moment-in-time but doesn't carry a date field.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "60 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
